import React, { useContext, useReducer, useCallback, useEffect } from 'react';
import { useApi } from '@cmr/react-api-hook';
import actions from './actions';
import { ISessionsContext, UserSession } from './types';
import reducer from './reducers';
import { useSignalR } from '../signalr/context';

const endpoint = `${process.env['REACT_APP_API_ENDPOINT']}sessions`;
export const SessionsContext = React.createContext<ISessionsContext | null>(
  null,
);
interface SessionsProviderOptions {
  children: React.ReactElement;
  sessionId: string;
}

export const useSessions = () => useContext(SessionsContext)!;

export const SessionsProvider = ({
  children,
  sessionId,
}: SessionsProviderOptions) => {
  const [state, localDispatch] = useReducer(reducer, { sessionId });
  const { apiFetch } = useApi();
  const { connection } = useSignalR();

  useEffect(() => {
    connection?.on('SessionChange', (payload: UserSession) => {
      localDispatch(actions.sessionChange(payload));
    });
    connection?.on('SessionEnded', (payload: string) =>
      localDispatch(actions.sessionEnded(payload)),
    );
  }, [connection]);

  //Some Inflow API calls expect a session header so we provide this helper function to add it.
  const sessionApiFetch = useCallback(
    (
      input: RequestInfo,
      dispatch?: React.Dispatch<any>,
      action?: any,
      init?: RequestInit,
      meta?: any,
    ) =>
      apiFetch(
        input,
        dispatch,
        action,
        { ...(init || {}), headers: { 'x-session-id': state.sessionId } },
        meta,
      ),
    [apiFetch, state.sessionId],
  );

  const fetch = useCallback(
    () => sessionApiFetch(endpoint, localDispatch, actions.fetch),
    [sessionApiFetch],
  );

  return (
    <SessionsContext.Provider
      value={{
        ...state,
        fetch,
        sessionApiFetch,
      }}
    >
      {children}
    </SessionsContext.Provider>
  );
};
